import styles from './Patrollers.module.css';

import { useState, useEffect, useMemo } from 'react';

import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Pagination from '@mui/material/Pagination';

import { getPatrollers, getCertifications } from "../../api.js";
import { useAuth } from '../../AuthContext';

import { Filter } from '../Filter/Filter';
import { Patroller } from '../Patroller/Patroller';
import { applyCertificationChange } from '../../utils/certificationChange.js';
import { matchesPatrollerSearch } from '../../utils/patrollerSearch.js';

export const Patrollers = ({ size = 10 }) => {
    const [ error, setError ] = useState(null);
    const [ loading, setLoading ] = useState(true);
    const [ patrollers, setPatrollers ] = useState([]);
    const [ page, setPage ] = useState(1);
    const [ filters, setFilters ] = useState({
        search: ''
    });

    const { token } = useAuth();

    const pageSize = Math.min(size, 10);

    const filtered = useMemo(() => {
        return patrollers.filter(patroller => matchesPatrollerSearch(patroller, filters.search));
    }, [ patrollers, filters ]);

    const currentPageData = useMemo(() => {
        const pageIndex = page - 1;

        return filtered.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
    }, [ filtered, page, pageSize ]);

    // Memoize pagination helpers
    const paginationInfo = useMemo(() => {
        const totalPages = Math.ceil(filtered.length / pageSize);

        return { totalPages };
    }, [ filtered.length, page, pageSize ]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({ search: '', days: [], onlyAvailable: false });
        setPage(1);
    };

    const handleOnCertify = (patrollerId, certification, certified) => {
        setPatrollers(prev => applyCertificationChange(prev, patrollerId, certification, certified));
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const patrollersData = await getPatrollers(token);
                const certificationsData = await getCertifications(token);

                if (!patrollersData || patrollersData.error) {
                    setError(patrollersData?.error || 'Failed to fetch patroller data. Please try again.');

                    return;
                }

                if (!certificationsData || certificationsData.error) {
                    setError(certificationsData?.error || 'Failed to fetch certification data. Please try again.');

                    return;
                }

                // merge patrollers with their certifications details
                patrollersData.forEach(patroller => {
                    patroller.certifications = certificationsData?.filter(cert => cert.patrollers.includes(patroller.id)) || [];
                });

                const patrollerIndex = patrollersData.findIndex(p => p.id);
                const initialPage = patrollerIndex > 0 ? Math.floor(patrollerIndex / pageSize) + 1 : 1;

                setPatrollers(patrollersData);
                setPage(initialPage);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert variant="filled" severity="error">{ error }</Alert>;
    }

    return (
        <div className={ styles.patrollersContainer }>
            <div className={ styles.header }>
                <span>Patrollers</span>

                { paginationInfo.totalPages > 1 && <Pagination
                    color="primary"
                    count={ paginationInfo.totalPages }
                    page={ page }
                    onChange={ (event, value) => setPage(value) }
                /> }
            </div>

            <div className={ styles.content }>
                <Filter
                    filters={ filters }
                    onFilterChange={ handleFilterChange }
                    onClearFilters={ handleClearFilters }
                />

                { currentPageData.map(patroller => (
                    <Patroller key={ patroller.id } patroller={ patroller } onCertify={ handleOnCertify } />
                )) }
            </div>
        </div>
    );
};